import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { EXAM_INSTRUCTION_SECTIONS, QUESTION_PALETTE_LEGEND } from '../../instructions.data';
import { ExamDetailsApiData, InstructionSection, PaletteLegendItem } from '../../model/test-series.model';
import { OutlineButtonComponent } from '../../../../shared/components/outline-button/outline-button';
import { PrimaryButtonComponent } from '../../../../shared/components/primary-button/primary-button';
import { ExamEnvironmentCheckService } from '../../../../core/security/exam-environment-check.service';
import { MockTestSecurityService } from '../../../../core/security/mock-test-security.service';
import { ExamEventTrackerService } from '../../../../core/services/exam-event-tracker.service';
import { ImageEnum } from '../../../../shared/utils/image.enum';
import { sanitizeRichHtml } from '../../../../shared/utils/rich-html.util';
import { ExamSessionService, isExamInProgressElsewhere } from '../../services/exam-session.service';
import { TestSeriesService } from '../../services/test-series.service';

@Component({
  selector: 'app-instructions',
  imports: [PrimaryButtonComponent, OutlineButtonComponent],
  templateUrl: './instructions.html',
  styleUrl: './instructions.css',
})
export class InstructionsComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly testSeriesService = inject(TestSeriesService);
  private readonly examSessionService = inject(ExamSessionService);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly securityService = inject(MockTestSecurityService);
  private readonly environmentCheck = inject(ExamEnvironmentCheckService);
  private readonly eventTracker = inject(ExamEventTrackerService);
  private hadFullscreen = false;
  private isProceedingToExam = false;
  private readonly instructionsScroll = viewChild<ElementRef<HTMLDivElement>>('instructionsScroll');

  protected readonly paletteLegend: PaletteLegendItem[] = QUESTION_PALETTE_LEGEND;
  protected readonly candidateInitial = 'R';
  protected readonly candidateName = 'Rishabh Singh';
  protected readonly hasAcceptedAgreement = signal(false);
  protected readonly isStartingExam = signal(false);
  protected readonly hasScrolledToBottom = signal(false);
  protected readonly popupMode = signal<'agreement' | 'fullscreen-exit' | 'system-check' | null>(null);
  protected readonly isCheckingEnvironment = signal(false);
  protected readonly environmentIssues = signal<string[]>([]);
  protected readonly imageEnum = ImageEnum;
  protected readonly examDetails = signal<ExamDetailsApiData | null>(null);
  protected readonly examDurationMinutes = computed(() => this.examDetails()?.durationMinutes ?? 0);
  protected readonly examTitle = computed(() => this.examDetails()?.examName ?? 'this test');
  protected readonly totalQuestions = computed(() => this.examDetails()?.totalQuestions ?? 0);
  protected readonly maximumMarks = computed(() => this.examDetails()?.maximumMarks ?? 0);
  protected readonly correctAnswerMarks = computed(() => this.examDetails()?.correctAnswerMarks ?? 0);
  protected readonly wrongAnswerMarks = computed(() =>
    this.examDetails()?.enableNegativeMarking ? Math.abs(this.examDetails()?.wrongAnswerMarks ?? 0) : 0,
  );
  /** Admin-configured parts drive the wording; the legacy fallback split is called "groups". */
  protected readonly hasConfiguredParts = computed(() =>
    Boolean(this.examDetails()?.testParts?.enabled && this.examDetails()?.testParts?.parts?.length),
  );
  private readonly partTerm = computed(() => (this.hasConfiguredParts() ? 'part' : 'group'));
  /** Numbered items in the General Instructions block, used to continue numbering below it. */
  protected readonly generalItemCount = 5;
  // Admin-authored instructions HTML from the exam details API, rendered as-is
  // (with its own inline/<style> CSS) below the default instructions.
  private readonly sanitizedApiInstructions = computed(() =>
    sanitizeRichHtml(this.examDetails()?.instructions),
  );
  protected readonly apiInstructionsHtml = computed<SafeHtml | null>(() => {
    const html = this.sanitizedApiInstructions();
    // Already sanitized above; bypass so Angular keeps the style attributes.
    return html ? this.domSanitizer.bypassSecurityTrustHtml(html) : null;
  });
  /** Plain text of the admin instructions, normalized for duplicate checks. */
  private readonly apiInstructionsText = computed(() => {
    const html = this.sanitizedApiInstructions();

    if (!html) {
      return '';
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('style').forEach((style) => style.remove());
    return this.normalizeText(doc.body.textContent ?? '');
  });
  protected readonly examParts = computed(() =>
    this.testSeriesService.buildExamParts(
      this.totalQuestions(),
      this.examDurationMinutes(),
      this.examDetails()?.testParts,
    ),
  );
  // Default sections the admin instructions already contain are dropped so
  // they aren't shown twice. "parts" always stays: it's built from the exam's
  // real part configuration.
  protected readonly sections = computed<InstructionSection[]>(() =>
    EXAM_INSTRUCTION_SECTIONS.filter(
      (section) => section.id === 'parts' || !this.isInApiInstructions(section),
    ).map((section) => {
      if (section.id !== 'parts') {
        return section;
      }

      const term = this.partTerm();
      const partCount = this.examParts().length;

      return {
        ...section,
        title: this.hasConfiguredParts() ? 'Parts:' : section.title,
        items: [
          `This test is divided into ${partCount} ${term}${partCount === 1 ? '' : 's'} - ${this.getFormattedPartLabels()}.`,
          `Each ${term} will have ${this.getPartQuestionText()} and is allotted ${this.getPartDurationText()}.`,
          `You cannot proceed to the next ${term} until you complete the allotted time of the previous ${term}.`,
          `The next ${term} will start automatically after the completion of the previous ${term}.`,
          `You cannot review/modify your responses for a ${term} after you complete the allotted time.`,
          `The test will be auto submitted after ${this.examDurationMinutes()} minutes.`,
          'You cannot use multiple devices to take this test.',
          'Try to stay in full-screen mode and avoid switching to another tab, window, or app during the test for the best experience.',
        ],
      };
    }),
  );

  ngOnInit(): void {
    this.hadFullscreen = Boolean(document.fullscreenElement);

    const examId = this.route.snapshot.paramMap.get('id');

    if (!examId) {
      return;
    }

    // Reopens the socket if the attempt is already started (page reload);
    // a new test is only started on "Proceed", so this is a no-op then.
    this.examSessionService.beginEventTracking();
    this.eventTracker.track('INSTRUCTIONS_OPENED');

    this.testSeriesService.getExamDetails(examId).subscribe({
      next: (examDetails) => {
        this.examDetails.set(examDetails);
        this.securityService.setProctoringEnabled(
          this.testSeriesService.extractExamSettings(examDetails).enableProctoring,
        );
        // Content height changes once API instructions render; re-check if
        // the user can already see the bottom without scrolling.
        setTimeout(() => this.checkScrolledToBottom(this.instructionsScroll()?.nativeElement ?? null));
      },
      error: () => {
        // Keep fallback static content if the details API fails.
      },
    });
  }

  ngOnDestroy(): void {
    this.eventTracker.track('INSTRUCTIONS_CLOSED');

    // Left the instructions without starting (Exit, back button): close the
    // attempt's event session. The question paper keeps it open otherwise.
    if (!this.isProceedingToExam) {
      this.examSessionService.endEventTracking();
    }
  }

  ngAfterViewInit(): void {
    this.checkScrolledToBottom(this.instructionsScroll()?.nativeElement ?? null);
  }

  protected onInstructionsScroll(event: Event): void {
    this.checkScrolledToBottom(event.target as HTMLElement);
  }

  private checkScrolledToBottom(element: HTMLElement | null): void {
    if (!element) {
      return;
    }

    const reachedBottom = element.scrollHeight - element.scrollTop - element.clientHeight <= 4;

    if (reachedBottom && !this.hasScrolledToBottom()) {
      this.hasScrolledToBottom.set(true);
    }
  }

  protected toggleAgreement(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.hasAcceptedAgreement.set(input.checked);
  }

  protected async proceedToExam(): Promise<void> {
    if (!this.hasAcceptedAgreement()) {
      this.popupMode.set('agreement');
      return;
    }

    if (this.isCheckingEnvironment() || this.isStartingExam()) {
      return;
    }

    this.isCheckingEnvironment.set(true);
    const issues = await this.environmentCheck.findIssues(
      this.testSeriesService.extractExamSettings(this.examDetails()).enableProctoring,
    );
    this.isCheckingEnvironment.set(false);
    this.environmentIssues.set(issues);

    // The exam only starts once every problem has been fixed; "Check Again"
    // in the popup re-runs this whole method.
    if (issues.length) {
      this.popupMode.set('system-check');
      return;
    }

    if (this.popupMode() === 'system-check') {
      this.popupMode.set(null);
    }

    const testId = this.route.snapshot.paramMap.get('id');

    if (!testId) {
      void this.router.navigate(['/test-series']);
      return;
    }

    this.isStartingExam.set(true);

    // The exam timer starts here, not on the Start Test popup, so time spent
    // reading the instructions isn't counted.
    const activeExamMeta = this.examSessionService.activeExamMeta();

    if (activeExamMeta?.examId === testId && !activeExamMeta.attemptId) {
      this.examSessionService.startAttempt(activeExamMeta).subscribe({
        next: () => this.navigateToQuestionPaper(testId),
        error: (error: unknown) => {
          this.isStartingExam.set(false);

          // Live on another device/platform (toasted by the error interceptor):
          // it can't be started from here, so send the student back.
          if (isExamInProgressElsewhere(error)) {
            this.exitToTestSeries();
          }
        },
      });
      return;
    }

    this.navigateToQuestionPaper(testId);
  }

  private navigateToQuestionPaper(testId: string): void {
    this.isProceedingToExam = true;
    void this.router.navigate(['/test-series', testId, 'question-paper'], { replaceUrl: true }).then((navigated) => {
      this.isStartingExam.set(false);

      if (!navigated) {
        this.isProceedingToExam = false;
      }
    });
  }

  protected closePopup(): void {
    const wasAgreement = this.popupMode() === 'agreement';
    this.popupMode.set(null);

    if (wasAgreement) {
      setTimeout(() => {
        const ndaSection = document.getElementById('nda-section');
        if (ndaSection) {
          ndaSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
          ndaSection.classList.add('bg-mock-amber-100');
          setTimeout(() => {
            ndaSection.classList.remove('bg-mock-amber-100');
          }, 1500);
        }
      }, 50);
    }
  }

  protected async resumeFullScreen(): Promise<void> {
    this.popupMode.set(null);

    const rootElement = document.documentElement;

    if (!rootElement.requestFullscreen) {
      return;
    }

    try {
      await rootElement.requestFullscreen();
      this.hadFullscreen = true;
    } catch {
      this.popupMode.set('fullscreen-exit');
    }
  }

  protected exitToTestSeries(): void {
    this.popupMode.set(null);
    void this.router.navigate(['/test-series']);
  }

  /** parts → navigation → answering continue the General Instructions numbering. */
  protected getSectionStart(sectionId: InstructionSection['id']): number | null {
    const continuedSectionIds = ['parts', 'navigation', 'answering'];

    if (!continuedSectionIds.includes(sectionId)) {
      return null;
    }

    let start = this.generalItemCount + 1;

    for (const section of this.sections()) {
      if (section.id === sectionId) {
        return start;
      }

      if (continuedSectionIds.includes(section.id)) {
        start += section.items.length;
      }
    }

    return null;
  }

  // Not a proctoring check — no counting, no warning, no auto-submit. This
  // just surfaces the "resume / exit" prompt (with its close cross) so the
  // student always has a visible, deliberate way back to fullscreen or out
  // of the exam, however many times they leave fullscreen.
  @HostListener('document:fullscreenchange')
  protected onFullscreenChange(): void {
    if (this.hadFullscreen && !document.fullscreenElement) {
      this.hadFullscreen = false;
      this.popupMode.set('fullscreen-exit');
    }
  }

  private getFormattedPartLabels(): string {
    const labels = this.examParts().map((part) => part.label);

    if (!labels.length) {
      return 'Group A';
    }

    if (labels.length === 1) {
      return labels[0];
    }

    if (labels.length === 2) {
      return `${labels[0]} & ${labels[1]}`;
    }

    return `${labels.slice(0, -1).join(', ')} & ${labels[labels.length - 1]}`;
  }

  private getPartQuestionText(): string {
    const questionCounts = this.examParts().map((part) => Number.parseInt(part.info, 10) || 0);
    const uniqueQuestionCounts = Array.from(new Set(questionCounts));

    if (uniqueQuestionCounts.length <= 1) {
      return `${questionCounts[0] ?? 0} questions`;
    }

    return `${this.joinWithOr(uniqueQuestionCounts)} questions`;
  }

  private getPartDurationText(): string {
    const durations = this.examParts().map((part) => part.durationInMinutes);
    const uniqueDurations = Array.from(new Set(durations));

    if (uniqueDurations.length <= 1) {
      return `${durations[0] ?? 0} minutes`;
    }

    return `${this.joinWithOr(uniqueDurations)} minutes`;
  }

  /** A section is repeated when the admin text has its heading or every one of its points. */
  private isInApiInstructions(section: InstructionSection): boolean {
    const apiText = this.apiInstructionsText();

    if (!apiText) {
      return false;
    }

    const title = this.normalizeText(section.title).replace(/:$/, '');

    return (
      apiText.includes(title) ||
      section.items.every((item) => apiText.includes(this.normalizeText(item)))
    );
  }

  private normalizeText(text: string): string {
    return text.replace(/\s+/g, ' ').trim().toLowerCase();
  }

  protected getQuestionCount(info: string): number {
    return Number.parseInt(info, 10) || 0;
  }

  /** [10, 20, 30] → "10, 20 or 30" */
  private joinWithOr(values: number[]): string {
    const sorted = [...values].sort((a, b) => a - b);

    if (sorted.length <= 1) {
      return `${sorted[0] ?? 0}`;
    }

    return `${sorted.slice(0, -1).join(', ')} or ${sorted[sorted.length - 1]}`;
  }
}
